import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type { UndefinedIdentityCellConfig } from '@/components/IdentityAvatarGrid';
import type { ActionMenuItem } from '@/components/EntityActionsMenu/EntityActionsMenu';
import type { Identity, IdentityToken } from '@/types/identity';
import type { ReportConfidenceRange, ReportGroupByMode } from '../constants';
import { ReportSidebarIdentities } from './ReportSidebarIdentities';

interface ReportSidebarProps {
    groupBy: ReportGroupByMode;
    onClearGroupBy: () => void;
    confidence: ReportConfidenceRange;
    identities: Identity[];
    selectedIdentityTokens: Set<IdentityToken>;
    onToggleIdentity: (token: IdentityToken) => void;
    onClearIdentitySelection: () => void;
    undefinedIdentityCells: UndefinedIdentityCellConfig[];
    buildIdentityMenuItems: (identity: Identity) => ActionMenuItem[];
}

export function ReportSidebar({
    groupBy,
    onClearGroupBy,
    confidence,
    identities,
    selectedIdentityTokens,
    onToggleIdentity,
    onClearIdentitySelection,
    undefinedIdentityCells,
    buildIdentityMenuItems,
}: ReportSidebarProps) {
    const { t } = useTranslation(I18nNamespace.REPORTS);

    const groupByCriterionLabel =
        groupBy === 'prompt'
            ? t('detail.groupBy.sidebarCriterionPrompt')
            : groupBy === 'source'
              ? t('detail.groupBy.sidebarCriterionSource')
              : null;

    return (
        <aside className="flex min-h-0 w-full flex-col">
            <div className="flex min-h-0 flex-col gap-6">
                <div className="flex flex-col gap-4">
                    <span className="text-sm-medium text-white-100">
                        {t('detail.confidence.sidebarLabel')}
                    </span>
                    <span className="text-sm font-normal text-grey-200 truncate">
                        {t('detail.confidence.sidebarRange', {
                            min: confidence.min,
                            max: confidence.max,
                        })}
                    </span>
                </div>
                {groupByCriterionLabel ? (
                    <div className="flex flex-col gap-4">
                        <div className="flex items-center justify-between">
                            <span className="text-sm-medium text-white-100">
                                {t('detail.groupBy.sidebarTitle')}
                            </span>
                            <button
                                type="button"
                                onClick={onClearGroupBy}
                                className="text-sm-medium text-grey-300 hover:text-white-100 transition-colors"
                            >
                                {t('detail.groupBy.clear')}
                            </button>
                        </div>
                        <span className="text-sm font-normal text-grey-200 truncate">
                            {groupByCriterionLabel}
                        </span>
                    </div>
                ) : null}
                <ReportSidebarIdentities
                    identities={identities}
                    selectedTokens={selectedIdentityTokens}
                    onToggle={onToggleIdentity}
                    onClear={onClearIdentitySelection}
                    undefinedCells={undefinedIdentityCells}
                    buildMenuItems={buildIdentityMenuItems}
                />
            </div>
        </aside>
    );
}
